<template>
  <section class="skin-chart">
    <div class="skin-chart_wrap card-dark">
      <div class="f fw gap8">
        <p class="h2">
          {{ t("value_history") }}
        </p>
        <div class="skin-chart_periods card-dark p8 r8 f gap4">
          <button
            v-for="p of periods"
            :key="p"
            :class="p === currPeriod ? 'active' : null"
            class="btn-default p0 wh36"
            @click="choosePeriod(p)"
          >
            {{ t(p.toUpperCase()) }}
          </button>
        </div>
      </div>
      <div class="skin-chart__body">
        <div
          v-if="loading"
          class="chart f fc"
        >
          <i class="i-loading mr10 green" />
          {{ t("inventory.loading_data") }}...
        </div>
        <client-only v-else-if="localChart?.length">
          <LazyPagesInventoryInnerChart
            :data="localChart?.length ? localChart : []"
          />
        </client-only>
        <div
          v-else
          class="chart f fc fcol pb50 gray"
        >
          <i class="i-logo green f70 mb16 op02" />
          <p>{{ t("inventory.no_data") }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { InventoryValueChartPeriod } from '~/graphql/generated/schema-types';
import type { Period } from '~/types/Period';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EventBus } from '~/composables/bus';
import { InventoryValueChartPeriod as PeriodGraph } from '~/graphql/generated/schema-types';
import { getInventoryValueChart } from '~/utils/api';

const props = withDefaults(
  defineProps<{
    market?: string
  }>(),
  {
    market: 'waxpeer',
  },
);

const periodsType: { [key in InventoryValueChartPeriod]: PeriodGraph } = {
  '1M': PeriodGraph.Month,
  '3M': PeriodGraph.ThreeMonth,
  'ALL': PeriodGraph.All,
};

const { t } = useI18n();
const debounce = useDebounce();
const route = useRoute();

const localChart = ref();
const periods: Period[] = ['1M', '3M', 'ALL'];
const currPeriod = ref<Period>('1M');
const loading = ref(false);

const getData = debounce(async () => {
  loading.value = true;

  try {
    const { data, errors } = await getInventoryValueChart(
      route.params.id as string,
      periodsType[currPeriod.value as InventoryValueChartPeriod],
      props.market,
    );

    if (errors?.length) {
      throw new Error(errors[0].message);
    }
    if (data.inventoryValueChart) {
      localChart.value = data.inventoryValueChart;
    }
  } catch (e) {
    console.log('getInventoryValueChart error: ', (e as Error).message);
    EventBus.$emit('notify', {
      success: false,
      msg: (e as Error).message || 'An error occurred',
    });
  } finally {
    loading.value = false;
  }
}, 600);

function choosePeriod(p: Period) {
  currPeriod.value = p;

  getData();
}

getData();
</script>

<style lang="scss" scoped>
.skin-chart {
  &_wrap {
    padding: 24px;
  }

  .h2 {
    font-size: 18px;
    font-weight: 600;
    color: var(--accent-on-accent-secondary);
  }

  &_periods {
    border-radius: 8px;
    background: var(--surfaces-high-container);
    flex-direction: row-reverse;
    padding: 8px;
    gap: 4px;
    margin-left: auto;
    width: max-content;

    button {
      text-transform: capitalize;
    }

    .active {
      background: var(--accent-secondary);
      border-radius: .8rem;
      color: var(--accent-on-accent-secondary);
      //color: var(--accent-on-primary);
    }
  }

  &_btn {
    color: var(--surfaces-on-surface);

    &.active {
      background: var(--surfaces-on-surface);
      color: var(--accent-on-accent-secondary);
      //color: var(--accent-on-primary);
    }
  }

  .chart {
    min-height: 41rem;
    margin-top: 1rem;
    margin-left: -4rem;

    &-tooltip {
      .date {
        color: var(--surfaces-on-surface-variable);
        font-size: 1.2rem;
      }
    }
  }

  @media screen and (max-width: 850px) {
    &_wrap {
      padding: 16px 16px 0;
    }
    &_periods {
      margin-left: 0;
      margin-bottom: 8px;
    }
    .chart {
      min-height: 36rem;
      margin-top: 1rem;
      margin-left: -3.5rem;
      font-size: 1rem;
    }
  }
}
</style>
